import axios from 'axios'
import request from '@/utils/request'
import { menuCodeHeaders } from '@/utils/menuCodeHeaders'
import { CommonFileItemRes, CommonFileResourceType, CommonFileUrlItemRes, CompleteUploadRes, PrepareUploadRes } from './types'

/**
 * 공통 파일(첨부·동영상) API — RGR01·RGR02·RGR04 공용(LOGISPRD-12443).
 * 근거: `oms-api/.../controller/commonfile/CommonFileController.kt`.
 */
export const COMMON_FILES_URL = '/api/v1/common/files'
export const COMMON_FILE_URLS_URL = '/api/v1/common/file/urls'
export const COMMON_FILE_UPLOADS_URL = '/api/v1/common/file/uploads'

/**
 * 공통 파일 API 를 부르는 2.0 화면코드. BE 가 X-Menu-Code 로 감사 MENU_CD 를 가르고, 헤더가 없으면 기록하지 않는다(LOGISPRD-17198·17199).
 */
// GL17(입고작업 2.0)은 준비 API 만 부른다. BE 공통 파일 감사의 screens 에 아직 GL17 이 없어 그 호출은 감사에 남지 않는다.
// 남겨야 하면 BE CommonFileController 의 screens 에 GL17 을 더한다.
// GL18(입고작업조회 2.0)은 조회·presign 만 부른다. BE screens 에 GL18 을 더했다(LOGISPRD-16058).
export type CommonFileMenuCode = 'RGR15' | 'RGR16' | 'RGR17' | 'RGR18' | 'RGR20' | 'RP14' | 'GL17' | 'GL18'

/** BE `MAX_PRESIGN_KEYS_SIZE` — 한 요청에 이보다 많은 keys 를 보내면 400. */
const MAX_PRESIGN_KEYS = 100

/** `fileIds` 는 조회 결과 행 수만큼 늘어 GET 쿼리면 URL 길이를 넘는다 — 본문(POST)으로 보낸다(LOGISPRD-14909). BE 는 건수 상한이 없다. */
export const getCommonFiles = (menuCode: CommonFileMenuCode, shipper: string, fileIds: string[]) =>
  request.post<ApiResponseV2<{ items: CommonFileItemRes[] }>, { shipper: string; fileIds: string[] }>(
    `${COMMON_FILES_URL}/search`,
    { shipper, fileIds },
    menuCodeHeaders(menuCode),
  )

/**
 * `keys` 를 100개씩 나눠 보내고 응답 items 를 순서대로 합친다. 배치 하나라도 실패하면 전체를
 * 실패로 던진다 — 호출부는 실패를 한 번에 처리한다.
 */
export const getCommonFileUrls = async (menuCode: CommonFileMenuCode, shipper: string, keys: string[]): Promise<CommonFileUrlItemRes[]> => {
  const chunks = Array.from({ length: Math.ceil(keys.length / MAX_PRESIGN_KEYS) }, (_, i) =>
    keys.slice(i * MAX_PRESIGN_KEYS, (i + 1) * MAX_PRESIGN_KEYS),
  )
  const responses = await Promise.all(
    chunks.map((chunk) =>
      request.post<ApiResponseV2<{ items: CommonFileUrlItemRes[] }>, { shipper: string; keys: string[] }>(
        COMMON_FILE_URLS_URL,
        { shipper, keys: chunk },
        menuCodeHeaders(menuCode),
      ),
    ),
  )
  return responses.flatMap((res) => res.data.items)
}

export const prepareCommonFileUpload = (menuCode: CommonFileMenuCode, shipper: string, fileName: string, contentType: string) =>
  request.post<ApiResponseV2<PrepareUploadRes>, { shipper: string; fileName: string; contentType: string }>(
    COMMON_FILE_UPLOADS_URL,
    { shipper, fileName, contentType },
    menuCodeHeaders(menuCode),
  )

export const completeCommonFileUpload = (
  menuCode: CommonFileMenuCode,
  shipper: string,
  key: string,
  resourceType: CommonFileResourceType,
  targetIds: number[],
) =>
  request.patch<ApiResponseV2<CompleteUploadRes>, { shipper: string; key: string; resourceType: CommonFileResourceType; targetIds: number[] }>(
    COMMON_FILE_UPLOADS_URL,
    { shipper, key, resourceType, targetIds },
    menuCodeHeaders(menuCode),
  )

/**
 * 실제 바이트 업로드 — presigned PUT. `request` 인스턴스를 타지 않는다(3.5) — S3 서명은
 * `signedHeaders` 로 이미 완결돼 있어 `request` 의 Authorization·Content-Type 주입이 서명을 깬다.
 */
export const putToPresignedUrl = (uploadUrl: string, file: File, signedHeaders: Record<string, string>) =>
  axios.put(uploadUrl, file, { headers: signedHeaders })

export type CommonFileUploadStage = '준비' | '전송' | '완료'

/**
 * 업로드 실패 — 어느 단계에서 몇 번 상태로 실패했는지만 담는다(LOGISPRD-14590). 네트워크 오류는 `status` 없음.
 * 원 오류(AxiosError)는 `config` 에 서명 URL·헤더를 품고 있어 들고 다니지 않는다 — 문구·콘솔로 새지 않게.
 */
export class CommonFileUploadError extends Error {
  constructor(
    readonly stage: CommonFileUploadStage,
    readonly status: number | undefined,
    readonly code: string | undefined,
  ) {
    super(status ? `${stage} 단계, ${status}` : `${stage} 단계`)
    this.name = 'CommonFileUploadError'
  }
}

const atStage = async <T>(stage: CommonFileUploadStage, run: () => Promise<T>): Promise<T> => {
  try {
    return await run()
  } catch (e) {
    throw new CommonFileUploadError(stage, axios.isAxiosError(e) ? e.response?.status : undefined, axios.isAxiosError(e) ? e.code : undefined)
  }
}

/**
 * 업로드 앞 2단계 — prepare → presigned PUT. 올린 key 를 돌려준다.
 * 업무 데이터 연결(완료)을 공통 API 가 아닌 업무 API 가 맡는 화면(GL17 입고작업 첨부 등)이 이것만 쓴다.
 */
export const putCommonFileObject = async (menuCode: CommonFileMenuCode, shipper: string, file: File): Promise<string> => {
  const { key, uploadUrl, signedHeaders } = await atStage(
    '준비',
    async () => (await prepareCommonFileUpload(menuCode, shipper, file.name, file.type || 'application/octet-stream')).data,
  )
  await atStage('전송', () => putToPresignedUrl(uploadUrl, file, signedHeaders))
  return key
}

/**
 * 업로드 3단계 — prepare → presigned PUT → complete. 중간에 실패하면 그 자리에서 멈추고
 * `CommonFileUploadError` 를 던진다 (완결되지 않은 파일은 `targetIds` 에 연결되지 않아 화면에 나타나지 않는다).
 */
export const uploadCommonFile = async (
  menuCode: CommonFileMenuCode,
  shipper: string,
  file: File,
  resourceType: CommonFileResourceType,
  targetIds: number[],
): Promise<CompleteUploadRes> => {
  const key = await putCommonFileObject(menuCode, shipper, file)
  const completed = await atStage('완료', () => completeCommonFileUpload(menuCode, shipper, key, resourceType, targetIds))
  return completed.data
}
